import type { TOfficeNotificationKey } from '~/domain/OfficeNotification';
import { ConfirmCandidateSource, type TConfirmCandidateSource } from './types';

/**
 * Reading the confirm source off the two email link families that lead into the
 * confirm flow. Both are emails — the portal has no push channel — but they are
 * minted by different senders and carry different params, so each has to be read
 * on its own terms.
 *
 * 1. **Office-notification CTAs** (GOTU-4135): `utm_source=email` +
 *    `utm_campaign=<notification key>`, landing on `/dashboard/job/:jobId`.
 * 2. **Confirmation-outreach links** (GOTU-4392): Branch links carrying
 *    `utm_medium=confirm-professional`, landing straight on the confirm route.
 */

/** Query param carrying which campaign a link belongs to. */
export const UTM_MEDIUM_PARAM = 'utm_medium';

/**
 * `utm_medium` on the confirmation-outreach links the BE mints at application
 * time. Note this is `utm_medium`, not the office-notification contract's
 * `utm_campaign`, and these links carry no `utm_source=email`.
 */
export const CONFIRM_OUTREACH_UTM_MEDIUM = 'confirm-professional';

/**
 * Maps an office-notification email key (the `utm_campaign` value read by
 * `parseNotificationParams`) to the confirm source it represents.
 *
 * Only the two candidate-applied campaigns lead into the confirm flow;
 * `offer_expiration_warning` lands on the Action Needed list and
 * `end_of_day_review` on a completed shift, so both return `undefined` rather
 * than being forced into a source value. An office who then reaches the confirm
 * screen from the Action Needed list gets `action_needed` from that click, which
 * is what actually drove the confirmation.
 */
export function confirmSourceFromEmailNotification(
  notification?: TOfficeNotificationKey,
): TConfirmCandidateSource | undefined {
  switch (notification) {
    case 'candidate_applied':
      return ConfirmCandidateSource.EmailCandidateApplied;
    case 'candidate_applied_counter':
      return ConfirmCandidateSource.EmailCandidateAppliedCounter;
    default:
      return undefined;
  }
}

/**
 * Whether this landing came from a confirmation-outreach link.
 *
 * The link carries no signal about whether the professional countered — both the
 * plain and the counter variant use the same `utm_medium`, differing only in which
 * shift they point at. So the applied/counter split has to come from the shift
 * itself; see {@link confirmOutreachSourceForJobStatus}.
 */
export function isConfirmOutreachLanding(searchParams: URLSearchParams): boolean {
  return searchParams.get(UTM_MEDIUM_PARAM) === CONFIRM_OUTREACH_UTM_MEDIUM;
}

/**
 * Picks the email source for a confirmation-outreach landing from the shift's
 * status.
 *
 * `counter` vs `pending` is the distinction the rest of the app already branches
 * on for this exact pair of states (the `/dashboard/job/:jobId` redirect, and the
 * mobile Schedule's `getScreenNameByStatus`), and it mirrors how the
 * `office_notification` events split offer-received from counter-offer-received.
 *
 * Returns `undefined` for any other status: the shift is no longer awaiting a
 * confirm decision, so attributing a confirm to the outreach email would be
 * guessing.
 */
export function confirmOutreachSourceForJobStatus(
  status?: string,
): TConfirmCandidateSource | undefined {
  if (status === 'counter') {
    return ConfirmCandidateSource.EmailCandidateAppliedCounter;
  }

  if (status === 'pending') {
    return ConfirmCandidateSource.EmailCandidateApplied;
  }

  return undefined;
}
